import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import api from '../services/api';
import './SubscriptionManager.css';

// Initialize Stripe (replace with your publishable key)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_stripe_publishable_key');

const SubscriptionManager = () => {
  const [subscriptions, setSubscriptions] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [message, setMessage] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(null);

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchPaymentHistory();
    
    // Check for successful subscription from URL params
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('subscription_success') === 'true') {
      const subscriptionType = urlParams.get('subscription_type');
      setSubscriptionSuccess(subscriptionType);
      setShowThankYou(true);
      // Clean up URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await api.get('/payment/subscription-status');
      setSubscriptions(response.data.subscriptions);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      setMessage('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const response = await api.get('/payment/payment-history');
      setPaymentHistory(response.data.paymentHistory);
    } catch (error) {
      console.error('Error fetching payment history:', error);
    }
  };

  const handleSubscribe = async (subscriptionType) => {
    setProcessingPayment(true);
    setMessage('');

    try {
      const response = await api.post('/payment/create-checkout-session', {
        subscriptionType
      });

      // Open Stripe Checkout in new tab to avoid potential browser security issues
      window.open(response.data.checkoutUrl, '_blank');
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setMessage(error.response?.data?.error || 'Failed to start checkout process');
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async (subscriptionType) => {
    if (!window.confirm('Are you sure you want to cancel this subscription? It will remain active until the end of the current billing period.')) {
      return;
    }

    try {
      await api.post('/payment/cancel-subscription', { subscriptionType });
      setMessage('Subscription will be cancelled at the end of the billing period');
      fetchSubscriptionStatus();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setMessage(error.response?.data?.error || 'Failed to cancel subscription');
    }
  };

  const handleReactivateSubscription = async (subscriptionType) => {
    try {
      await api.post('/payment/reactivate-subscription', { subscriptionType });
      setMessage('Subscription reactivated successfully');
      fetchSubscriptionStatus();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      setMessage(error.response?.data?.error || 'Failed to reactivate subscription');
    }
  };

  const handleManageSubscriptions = async () => {
    try {
      const response = await api.post('/payment/create-portal-session');
      window.location.href = response.data.portalUrl;
    } catch (error) {
      console.error('Error creating portal session:', error);
      setMessage('Failed to open subscription management portal');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount, currency = 'gbp') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  if (loading) {
    return <div className="subscription-manager loading">Loading subscription data...</div>;
  }

  const getSubscriptionDisplayName = (subscriptionType) => {
    const typeMap = {
      'doubleXPWeekly': 'Double XP Weekly',
      'doubleXPMonthly': 'Double XP Monthly', 
      'doubleMiningWeekly': 'Double Mining Weekly',
      'doubleMiningMonthly': 'Double Mining Monthly',
      'comboMonthly': 'Combo Monthly Deal'
    };
    return typeMap[subscriptionType] || subscriptionType;
  };

  return (
    <div className="subscription-manager">
      <h3>💎 Premium Subscriptions</h3>
      
      {/* Thank You Modal */}
      {showThankYou && subscriptionSuccess && (
        <div className="thank-you-modal">
          <div className="modal-content">
            <div className="thank-you-header">
              <span className="thank-you-icon">🎉</span>
              <h2>Thank You for Subscribing!</h2>
            </div>
            <div className="thank-you-body">
              <p>Your <strong>{getSubscriptionDisplayName(subscriptionSuccess)}</strong> subscription is now active!</p>
              <p>You'll start receiving your premium benefits immediately:</p>
              <ul className="benefits-list">
                {subscriptionSuccess.includes('XP') && <li>✨ Double XP from all activities</li>}
                {subscriptionSuccess.includes('Mining') && <li>⛏️ Double mining collection rates</li>}
                {subscriptionSuccess === 'comboMonthly' && (
                  <>
                    <li>✨ Double XP from all activities</li>
                    <li>⛏️ Double mining collection rates</li>
                  </>
                )}
              </ul>
              <p className="subscription-status">
                <span className="status-badge active">🟢 SUBSCRIBED</span>
                Your subscription is active and benefits are enabled.
              </p>
            </div>
            <div className="thank-you-footer">
              <button 
                onClick={() => setShowThankYou(false)} 
                className="btn-primary"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}
      
      {message && (
        <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="subscription-plans">
        {/* Double XP Subscription */}
        <div className="subscription-card">
          <h4>⭐ Double XP</h4>
          <p className="description">Earn 2x XP from all activities</p>
          
          {subscriptions?.doubleXP?.active ? (
            <div className="active-subscription">
              <div className="subscription-status-header">
                <span className="badge active">🟢 SUBSCRIBED</span>
                <span className="subscription-type">{subscriptions.doubleXP.type === 'weekly' ? 'Weekly' : 'Monthly'}</span>
              </div>
              <div className="subscription-details">
                <p><strong>Benefits:</strong> ✨ Double XP from all activities</p>
                <p><strong>Next billing:</strong> {formatDate(subscriptions.doubleXP.currentPeriodEnd)}</p>
                {subscriptions.doubleXP.cancelAtPeriodEnd && (
                  <p className="warning">⚠️ Will cancel at period end</p>
                )}
              </div>
              <div className="subscription-actions">
                {subscriptions.doubleXP.cancelAtPeriodEnd ? (
                  <button 
                    onClick={() => handleReactivateSubscription('doubleXP')}
                    className="btn-secondary"
                  >
                    🔄 Reactivate Subscription
                  </button>
                ) : (
                  <button 
                    onClick={() => handleCancelSubscription('doubleXP')}
                    className="btn-danger"
                  >
                    ❌ Cancel Subscription
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="subscription-options">
              <button 
                onClick={() => handleSubscribe('doubleXPWeekly')}
                disabled={processingPayment}
                className="btn-primary subscribe-btn"
              >
                {processingPayment ? '⏳ Processing...' : '💳 Subscribe Weekly - £2/week'}
              </button>
              <button 
                onClick={() => handleSubscribe('doubleXPMonthly')}
                disabled={processingPayment}
                className="btn-primary subscribe-btn"
              >
                {processingPayment ? '⏳ Processing...' : '💳 Subscribe Monthly - £6/month'}
              </button>
            </div>
          )}
        </div>

        {/* Double Mining Rate Subscription */}
        <div className="subscription-card">
          <h4>⛏️ Double Mining Rate</h4>
          <p className="description">Mine WR Coins 2x faster</p>
          
          {subscriptions?.doubleMining?.active ? (
            <div className="active-subscription">
              <div className="subscription-status-header">
                <span className="badge active">🟢 SUBSCRIBED</span>
                <span className="subscription-type">{subscriptions.doubleMining.type === 'weekly' ? 'Weekly' : 'Monthly'}</span>
              </div>
              <div className="subscription-details">
                <p><strong>Benefits:</strong> ⛏️ Mine WR Coins 2x faster</p>
                <p><strong>Next billing:</strong> {formatDate(subscriptions.doubleMining.currentPeriodEnd)}</p>
                {subscriptions.doubleMining.cancelAtPeriodEnd && (
                  <p className="warning">⚠️ Will cancel at period end</p>
                )}
              </div>
              <div className="subscription-actions">
                {subscriptions.doubleMining.cancelAtPeriodEnd ? (
                  <button 
                    onClick={() => handleReactivateSubscription('doubleMining')}
                    className="btn-secondary"
                  >
                    🔄 Reactivate Subscription
                  </button>
                ) : (
                  <button 
                    onClick={() => handleCancelSubscription('doubleMining')}
                    className="btn-danger"
                  >
                    ❌ Cancel Subscription
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="subscription-options">
              <button 
                onClick={() => handleSubscribe('doubleMiningWeekly')}
                disabled={processingPayment}
                className="btn-primary subscribe-btn"
              >
                {processingPayment ? '⏳ Processing...' : '💳 Subscribe Weekly - £2/week'}
              </button>
              <button 
                onClick={() => handleSubscribe('doubleMiningMonthly')}
                disabled={processingPayment}
                className="btn-primary subscribe-btn"
              >
                {processingPayment ? '⏳ Processing...' : '💳 Subscribe Monthly - £6/month'}
              </button>
            </div>
          )}
        </div>

        {/* Combo Monthly Subscription */}
        <div className="subscription-card featured">
          <div className="ribbon">Best Value!</div>
          <h4>🎯 Combo Monthly Deal</h4>
          <p className="description">Get both Double XP + Double Mining Rate</p>
          <p className="savings">Save £2/month!</p>
          
          {subscriptions?.comboMonthly?.active ? (
            <div className="active-subscription">
              <div className="subscription-status-header">
                <span className="badge active">🟢 SUBSCRIBED</span>
                <span className="subscription-type">Monthly Combo</span>
              </div>
              <div className="subscription-details">
                <p><strong>Benefits:</strong></p>
                <ul className="combo-benefits">
                  <li>✨ Double XP from all activities</li>
                  <li>⛏️ Double mining collection rates</li>
                </ul>
                <p><strong>Next billing:</strong> {formatDate(subscriptions.comboMonthly.currentPeriodEnd)}</p>
                {subscriptions.comboMonthly.cancelAtPeriodEnd && (
                  <p className="warning">⚠️ Will cancel at period end</p>
                )}
              </div>
              <div className="subscription-actions">
                {subscriptions.comboMonthly.cancelAtPeriodEnd ? (
                  <button 
                    onClick={() => handleReactivateSubscription('combo')}
                    className="btn-secondary"
                  >
                    🔄 Reactivate Subscription
                  </button>
                ) : (
                  <button 
                    onClick={() => handleCancelSubscription('combo')}
                    className="btn-danger"
                  >
                    ❌ Cancel Subscription
                  </button>
                )}
              </div>
            </div>
          ) : (
            <button 
              onClick={() => handleSubscribe('comboMonthly')}
              disabled={processingPayment || subscriptions?.doubleXP?.active || subscriptions?.doubleMining?.active}
              className="btn-primary large subscribe-btn"
            >
              {processingPayment ? '⏳ Processing...' : '💳 Subscribe for £10/month'}
            </button>
          )}
          {(subscriptions?.doubleXP?.active || subscriptions?.doubleMining?.active) && !subscriptions?.comboMonthly?.active && (
            <p className="info">Cancel individual subscriptions first to get the combo deal</p>
          )}
        </div>
      </div>

      {/* Manage Subscriptions Button */}
      {(subscriptions?.doubleXP?.active || subscriptions?.doubleMining?.active || subscriptions?.comboMonthly?.active) && (
        <div className="manage-subscriptions">
          <button onClick={handleManageSubscriptions} className="btn-secondary">
            Manage All Subscriptions
          </button>
        </div>
      )}

      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <div className="payment-history">
          <h4>Payment History</h4>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Subscription</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {paymentHistory.map((payment, index) => (
                <tr key={index}>
                  <td>{formatDate(payment.createdAt)}</td>
                  <td>{payment.subscriptionType}</td>
                  <td>{formatCurrency(payment.amount, payment.currency)}</td>
                  <td className={`status ${payment.status}`}>{payment.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManager;